/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-lines */

import AssertionError from 'assertion-error';

import { assertIsNonEmptyArray } from '../../../util.type';
import {
  addDays,
  addYears,
  diffDays,
  fromYMD,
  getDayOfWeek,
  getYear,
  isLess as isLessDayDate,
  isLessOrEqual
} from '../../day-date';
import { skipToCurrentOrNext } from '../../day-interval/day-interval-generator';
import { SomeDaysOfWeek } from '../../day-of-week';
import { compose, firstOrNull } from '../../generator/generator-util';
import holidayGenerator from '../../holiday/holiday-generator';
import HolidayInterval from '../../holiday/holiday-interval';
import {
  addMs,
  formatISOLocalString,
  getDayDate,
  getISOTimeString
} from '../../iso-local-string';
import {
  compare as compareISOTime,
  getMillisecondsOfDay,
  isLess as isLessISOTime
} from '../../iso-time-string';
import { mapTimers, timerMax } from '../../power/power-parser';
import Queue from '../../queue/queue';

/**
 * @import { NonEmptyArray } from '../../../util.type'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { DayDate } from '../../day-date'
 * @import DayInterval from '../../day-interval/day-interval'
 * @import { DayOfWeek } from '../../day-of-week'
 * @import { ISOLocalString } from '../../iso-local-string'
 * @import { ISOTimeString } from '../../iso-time-string'
 * @import { PowerState, PowerTimer, TimersAndHoliday, WeekDayInterval } from '../../power'
 * @import PowerManager from '../../power/power-manager'
 */

/**
 * @typedef {Pick<ContentInfo, 'holidayIntervals' | 'power'>} IContentInfo
 */

/**
 * Maximální počet dní maximálně sloučeného prázdninového intervalu.
 * @see file://./../../holiday/README.md
 */
export const maxDays = 42;

/** Zpoždění pro plánované vypnutí zařízení (10 minut). */
const reverseShutdownDelay = 600000;
/** Bezpečnostní padding pro plánované probuzení (1 minuta). */
const safetyPadding = 60000;

/**
 * Nahrazovač úloh a časovačů pro vypnutí a zapnutí zařízení, včetně prázdnin.
 *
 * Stará se o aktualizaci časovačů zařízení a prázdnin dle obsahu datového skladu.
 */
export default class ReplacerPower {
  /**
   * Vytvoří nahrazovač časovačů a prázdnin.
   * @param {PowerManager} powerManager
   */
  constructor(powerManager) {
    this.powerManager = powerManager;
    this.canShutdown = false;
    /** @private Mutex pro synchronizaci power operací (zapnutí/vypnutí, časovače, prázdniny). */
    this.mutex = new Queue();
  }

  // TODO: Vyčlenit do multiplatformného kódu, kt. ale nie je replacer,
  // niečo iné.
  /**
   * @param {() => Promise<void>} fn
   */
  async doInMutex(fn) {
    await this.mutex.enqueue(fn);
  }

  /**
   * @private
   * @param {TimersAndHoliday} timersAndHoliday
   * @throws {Error | AggregateError}
   */
  async ensureTimersAndHolidayWithFallback(timersAndHoliday) {
    // Nastaví se nové časovače a případně i prázdniny. Pokud nastane chyba,
    // tak se zkusí bez prázdnin.
    // Pokud ani to nevyjde, tak se vyhodí AggregateError s oběma chybami.
    // Pokud vyjde některý z pokusů, tak se vyhodí původní chyba,
    // aby volající (a Sentry) věděl, že se vše nepovedlo podle plánu.
    // canShutdown se nastaví na true pouze při úspěšném nastavení časovačů,
    // aby bylo jisté, že je možné zařízení bezpečně uspávat s jistotou probuzení.
    this.canShutdown = false;
    try {
      await this.powerManager.ensureTimersAndHoliday(timersAndHoliday);
      this.canShutdown = true;
    } catch (err) {
      try {
        await this.powerManager.ensureTimersAndHoliday({
          ...timersAndHoliday,
          holidayInterval: null
        });
        this.canShutdown = true;
      } catch (e) {
        throw new AggregateError([err, e]);
      }
      throw err;
    }
  }

  /**
   * Restartuje zařízení pomocí {@link PowerManager.reboot} pod zámkem.
   * @returns {Promise<void>}
   */
  async reboot() {
    return await this.doInMutex(() => this.powerManager.reboot());
  }

  /**
   * Změní časovače zařízení a prázdniny dle datového skladu.
   * @param {IContentInfo} contentInfo
   * @param {ISOLocalString} [now = formatISOLocalString(new Date())] (Aktuální) den v lokální časové zóně.
   * @returns {Promise<void>} Vyřeší se po dokončení přeplánování.
   */
  async replace(contentInfo, now = formatISOLocalString(new Date())) {
    return await this.doInMutex(async () => {
      const timersAndHoliday = getTimersAndHolidayWithReverseShutdown(
        contentInfo,
        now
      );
      await this.ensureTimersAndHolidayWithFallback(timersAndHoliday);
    });
  }

  /**
   * Vypne zařízení pomocí {@link PowerManager.shutdown} pod zámkem.
   * @returns {Promise<void>}
   * @throws {Error} Pokud zařízení nelze bezpečně vypnout.
   */
  async shutdown() {
    await this.doInMutex(() => this.powerManager.shutdown());
  }

  /**
   * Vypne zařízení a naplánuje jeho probuzení.
   * @param {ISOLocalString} date
   * @param {ISOLocalString} now
   * @throws {RangeError} Pokud je datum probuzení v minulosti nebo příliš blízko (1 minuta).
   */
  async shutdownWithWakeUp(date, now = formatISOLocalString(new Date())) {
    const nowPadded = addMs(now, safetyPadding);
    validateWakeUpDate(date, nowPadded);
    // Každý command má vlastní mutex, jenže 'lcd' a 'shutdown' jsou
    // odlišné commandy, proto se musí použít zámek.
    await this.doInMutex(async () => {
      // 1. Vymažeme všechny časovače a prázdniny.
      await this.powerManager.ensureTimersAndHoliday({
        onTimers: [],
        offTimers: [],
        onDays: undefined,
        holidayInterval: null,
        now
      });
      // 2. Vypočítáme ON TIMER, délku prázdninového intervalu a OFF TIMER
      // pro okamžité vypnutí.
      const timersAndHoliday = getWakeUpSchedule(date, nowPadded);
      // 3. Naplánujeme ON TIMER a prázdniny.
      await this.powerManager.ensureTimersAndHoliday(timersAndHoliday);
    });
  }
}

/**
 * "Postcondition" kontrola vypočítaného prázdninového intervalu
 * a počtu získaných časovačů v {@link ReplacerPower.replace} a zároveň
 * precondition kontrola parametrů ve volání {@link PowerManager.ensureTimersAndHoliday}.
 *
 * Kontroluje se:
 * - počet ON časovačů i počet OFF časovačů musí být menší než {@link timerMax}
 * - pokud je zadaný `holidayInterval`
 *     nějaký nastavený (on) den v týdnu (**už se nekontroluje**: to zajišťuje typ získaný z {@link getTimersAndHoliday})
 *   - `holidayInterval`
 *     - délka může být maximálně `maxDays` dní
 *       - toto je zbytečné umělé omezení pro platformy s explicitním rokem
 *       - pro platformy s implicitním rokem musí taktéž platit `maxDays < 365`,
 *         jinak by mohlo dojít k modulo rok wraparroundu nebo vytvoření
 *         souvislého intervalu bez jediného dne probuzení (a tedy uspání navždy)
 *         - to je volbou `maxDays = 42` splněno a nemusí se složitě a podmíněně
 *           ošetřovat
 *     - musí začínat a končit na on dnu v týdnu (tj. musí být minimální)
 *     - konec nesmí být v minulosti vzhledem k {@link today}
 * @param {TimersAndHoliday} timersAndHoliday
 * @throws {TypeError} Pokud kombinace parametrů není validní.
 */
export function checkEnsureTimersAndHolidayParams(timersAndHoliday) {
  const { onTimers, offTimers, onDays, holidayInterval, now } =
    timersAndHoliday;
  const today = getDayDate(now);
  if (onTimers.length > timerMax || offTimers.length > timerMax) {
    throw new TypeError(
      `too many timers (on: ${onTimers.length}, off: ${offTimers.length})`
    );
  }
  if (!holidayInterval) {
    return;
  }

  if (
    // Interval nesmí být v minulosti.
    isLessDayDate(holidayInterval.end, today) ||
    // Interval nesmí začínat nebo končit na off dnu.
    !onDays.get(getDayOfWeek(holidayInterval.start)) ||
    !onDays.get(getDayOfWeek(holidayInterval.end)) ||
    // Interval nesmí mít délku větší jako maxDays.
    holidayInterval.days > maxDays
  ) {
    throw new TypeError(
      `invalid holidayInterval ${holidayInterval} (${today}, ${onDays})`
    );
  }
}

// TODO: Přidat další kontroly, které ověřují postcondition ve filterHolidayByPlanWindow.
/**
 * Postcondition pro {@link filterHolidayByPlanWindow}.
 *
 * U platforem s implicitním rokem zkontroluje, zda start intervalu není v minulých letech vzhledem k {@link today}.
 * @param {boolean} implicitYear
 * @param {DayInterval} holidayInterval
 * @param {DayDate} today
 * @throws {Error} Pokud je rok startu v minulosti. Toto je programátorská chyba a neměla by nastat.
 */
export function checkHolidayByPlanWindow(implicitYear, holidayInterval, today) {
  if (implicitYear && getYear(holidayInterval.start) < getYear(today)) {
    throw new Error(
      'holidayInterval start year is in the past for implicit year platform'
    );
  }
}

/**
 * @param {ISOLocalString} date
 * @param {PowerState} state
 * @returns {PowerTimer}
 */
function createTimer(date, state) {
  return {
    state,
    time: getISOTimeString(date),
    day: getDayOfWeek(getDayDate(date))
  };
}

/**
 * @param {HolidayInterval[]} intervals Prázdninové intervaly.
 * @param {SomeDaysOfWeek} onDays Sjednocení on dnů v týdnu přes všechny power tasky.
 * @param {DayDate} today Den (typicky dnešek), ve který se dělá plánování.
 * @param {Object} [opts = {}]
 * @param {(...data: any[]) => void} [opts.logger] Logger pro debugování.
 * @returns {DayInterval | null}
 */
function currentOrNextMergedInterval(
  intervals,
  onDays,
  today,
  { logger } = {}
) {
  return firstOrNull(
    compose(
      holidayGenerator(intervals, onDays, maxDays, { logger }),
      skipToCurrentOrNext(today)
    )
  );
}

/**
 * @param {DayOfWeek} target
 * @param {DayOfWeek} today
 * @returns {number}
 */
function daysUntil(target, today) {
  return (target - today + 7) % 7;
}

/**
 * Případně vyfiltruje (a upraví) interval podle druhu platformy.
 *
 * Obecně propustí prázdniny pouze tehdy, pokud byl nastavený nějaký časovač
 * zapnutí. Jinak by to bylo matoucí, jelikož prázdniny jen maskují on timery,
 * takže bez timerů v režimu 24/7 nejsou eventy, aby se zařízení mohlo uspat
 * a hlavně probudit.
 *
 * Pro platformy s implicitním rokem je nutné plánovací okno nastavit
 * na jeden rok od {@link today}, protože validní current or next holiday může
 * být např. až za 100 let, takže se nesmí tento rok brát v potaz. Zároveň
 * je nutné případně ořezat validní start v minulém roce na začátek tohoto
 * roku, jinak by došlo k nesprávnému aliasingu.
 *
 * Tato funkce má postcondition test v podobě {@link checkHolidayByPlanWindow}.
 * @param {boolean} implicitYear
 * @param {boolean} someOnTimers
 * @param {DayInterval | null} holidayInterval
 * @param {DayDate} today
 * @returns {DayInterval | null}
 */
export function filterHolidayByPlanWindow(
  implicitYear,
  someOnTimers,
  holidayInterval,
  today
) {
  if (!holidayInterval || !someOnTimers) {
    return null;
  }

  if (!implicitYear) {
    return holidayInterval;
  }

  // TODO: Nebo ještě o den míň, aby náhodou nevzniklo nechtěné uspání navždy?
  // Teoreticky modulo rok end na včerejšku nevadí, pokud start nebude na today,
  // což je omezením délky např. na 42 < 364 dní zaručeno.
  // TODO: Možná by chtělo testovat start, že není příliš v minulosti.
  // To by možná částečně a nepřímo řešilo problém předávání intervalů
  // v minulosti.
  if (isLessOrEqual(addYears(today, 1), holidayInterval.end)) {
    return null;
  }

  // Odfiltrování celého intervalu před začátkem stávajícího roku.
  // TODO: Pozor, taková situace (a obecně interval končící před today by nikdy
  // díky checkEnsureTimersAndHolidayParams neměla nastat, ale pro robustnost
  // a obecnou správnost ořezu startu níže vhodné ošetření.
  const currentYear = getYear(today);
  if (getYear(holidayInterval.end) < currentYear) {
    return null;
  }

  // Ořezání intervalu v minulosti na přelomu stávajícího roku.
  if (getYear(holidayInterval.start) < currentYear) {
    return holidayInterval.setStart(fromYMD(currentYear, 1, 1));
  }

  return holidayInterval;
}

/**
 * @param {PowerTimer[]} timers
 * @param {DayOfWeek} dayOfWeek
 * @param {ISOTimeString} nowTime
 * @returns {PowerTimer[]}
 */
function getActiveTimersForDay(timers, dayOfWeek, nowTime) {
  return timers.filter(
    ({ day, time }) => day === dayOfWeek && isLessISOTime(nowTime, time)
  );
}

/**
 * Získá nejbližší budoucí off timer pro daný den v týdnu a čas.
 * @param {PowerTimer[]} offTimers
 * @param {DayOfWeek} dayOfWeek
 * @param {ISOTimeString} nowTime
 * @returns {PowerTimer | undefined}
 */
function getNextOffTimer(offTimers, dayOfWeek, nowTime) {
  const sorted = offTimers.toSorted((a, b) => {
    const da = daysUntil(a.day, dayOfWeek);
    const db = daysUntil(b.day, dayOfWeek);

    // Ak je rovnaký deň, ale čas už prešiel, posuň to o týždeň.
    const adjDa = da === 0 && a.time <= nowTime ? 7 : da;
    const adjDb = db === 0 && b.time <= nowTime ? 7 : db;

    return adjDa - adjDb || compareISOTime(a.time, b.time);
  });

  return sorted[0];
}

/**
 * Získá optimalizovaný počet časovačů ze všech denních intervalů.
 * Pokud je počet intervalů menší nebo roven 7, vrátí všechny časovače
 * namapované z intervalů (2 časovače na interval), protože je jich maximálně 14.
 * Pro dnešní den vrací relevantní časovače podle aktuálního času
 * (podle funkce {@link getTimersOfCurrentDay}),
 * pro ostatní dny vrací pouze ON (první) časovač z prvního intervalu
 * a OFF (poslední) časovač z posledního intervalu.
 *
 * Maximálně vrátí 14 časovačů.
 * @param {WeekDayInterval[]} intervals Seřazené intervaly podle dne v týdnu a času.
 * @param {ISOLocalString} now
 * @returns {PowerTimer[]}
 */
export function getOptimizedTimers(intervals, now) {
  if (intervals.length <= 7) {
    return mapTimers(intervals);
  }
  const dayOfWeek = getDayOfWeek(getDayDate(now));
  return Object.entries(Object.groupBy(intervals, (t) => t.dayOfWeek)).flatMap(
    ([day, intervalsByDay]) =>
      Number(day) === dayOfWeek
        ? getTimersOfCurrentDay(intervalsByDay, getISOTimeString(now))
        : getTimersOfOtherDay(intervalsByDay)
  );
}

/**
 * Vypočítá spací režim s denní granularitou, reprezentovaný jako prázdninový interval.
 * Interval sahá od dne následujícího po dnešku až do dne před datem probuzení.
 * Pokud je rozdíl mezi daty menší nebo roven 1 dni, vrací null.
 * Prerekvizita: `date` > `now` + 1 minúta
 * @param {ISOLocalString} date
 * @param {ISOLocalString} now
 * @returns {DayInterval | null}
 */
function getSleepDayInterval(date, now) {
  const targetDayDate = getDayDate(date);
  const nowDayDate = getDayDate(now);
  if (diffDays(targetDayDate, nowDayDate) <= 1) {
    return null;
  }
  return currentOrNextMergedInterval(
    [
      HolidayInterval.from(
        addDays(nowDayDate, 1),
        addDays(targetDayDate, -1),
        false
      )
    ],
    // Není relevantní, proto nastavíme každý den jako ON.
    SomeDaysOfWeek.from(127),
    nowDayDate
  );
}

/**
 * Vypočítá dobu timeout pro off timer.
 * @param {PowerTimer} powerTimer
 * @param {ISOLocalString} now
 * @returns {number} Doba v milisekundách s přidanou 1 minutou jako ochranný padding.
 */
function getTimeoutDuration(powerTimer, now) {
  const nowTime = getISOTimeString(now);
  const today = getDayDate(now);
  const diff =
    getMillisecondsOfDay(powerTimer.time) - getMillisecondsOfDay(nowTime);
  const days = daysUntil(powerTimer.day, getDayOfWeek(today));
  return (days === 0 && diff <= 0 ? 7 : days) * 86400000 + diff + safetyPadding;
}

/**
 * @param {NonEmptyArray<WeekDayInterval>} intervals Seřazené intervaly podle dne v týdnu a času.
 * @param {ISOLocalString} now
 * @returns {[offTimers: PowerTimer[], onTimers: NonEmptyArray<PowerTimer>]}
 */
function getTimersByPowerState(intervals, now) {
  const timers = getOptimizedTimers(intervals, now);
  const onTimers = timers.filter(({ state }) => state === 'on');
  const offTimers = timers.filter(({ state }) => state === 'off');
  // Pokud je již power definován, onTimers není prázdné, jinak je to programátorská chyba.
  assertIsNonEmptyArray(onTimers);
  return [offTimers, onTimers];
}

/**
 * Vrátí 0 nebo 2 časovače (ON, OFF) pro jiný den než je dnes.
 * @param {WeekDayInterval[]} intervals Časové intervaly pro konkrétní den v týdnu seřazené podle času.
 * @returns {PowerTimer[]}
 */
function getTimersOfOtherDay(intervals) {
  const { length } = intervals;
  if (length === 0) {
    return [];
  }
  const firstInterval = intervals[0];
  const lastInterval = intervals[length - 1];
  const timers = mapTimers([firstInterval, lastInterval]);
  return [timers[0], timers[3]];
}

/**
 *
 * Vrátí 0 nebo 2 časovače pro aktuální den.
 *
 * Získá časovače pro daný den z denních intervalů podle aktuálního času.
 * Vrací pouze relevantné časovače - buď nadcházející ON časovač a následující
 * OFF časovač (z téhož intervaku), nebo aktuální OFF + následující ON časovač
 * (modulo den - pokud je aktuální interval posledný v dni, použije se první
 * interval téhož dne jako následující).
 * @param {WeekDayInterval[]} intervals Časové intervaly pro konkrétní den v týdnu seřazené podle času.
 * @param {ISOTimeString} nowTime
 * @returns {PowerTimer[]}
 */
function getTimersOfCurrentDay(intervals, nowTime) {
  const activeIntervals = intervals.filter(({ end }) =>
    isLessISOTime(nowTime, end)
  );
  // Pokud nejsou žádné aktivní intervaly, dnešek "už skončil",
  // proto se staví k dnešnímu dni jako k jinému dni.
  if (activeIntervals.length === 0) {
    return getTimersOfOtherDay(intervals);
  }
  // Pokud je první interval zahájen, ale ještě neskončil.
  if (!isLessISOTime(nowTime, activeIntervals[0].start)) {
    // Pokud je poslední interval, přidá se první interval pro cyklus.
    if (activeIntervals.length === 1) {
      activeIntervals.push(intervals[0]);
    }
    // Vrátí časovače pro aktuální a následující interval (OFF, ON).
    return mapTimers(activeIntervals.slice(0, 2)).slice(1, 3);
  }
  // Pokud interval ještě nezačal, vrátí první interval (ON, OFF).
  return mapTimers(activeIntervals.slice(0, 1));
}

/**
 * @param {IContentInfo} contentInfo
 * @param {ISOLocalString} now
 * @returns {TimersAndHoliday}
 */
function getTimersAndHoliday(contentInfo, now) {
  const today = getDayDate(now);
  const { power, holidayIntervals } = contentInfo;
  /** @type {DayInterval | null} */
  let holidayInterval = null;

  // Pokud není power, jedná se o režim 24/7 bez časovačů, ve kterém
  // neumíme prázdniny vynutit (kvůli typické implementaci
  // maskování on časovačů). Navíc algoritmus sloučení a získání probíhajícího
  // nebo nejbližšího intervalu a ořezání na maximální délku potřebuje prkriciávě
  // alespoň jeden den v týdnu být zapnutý, jinak by došlo k iteraci do nekonečna!
  if (!power) {
    return {
      onTimers: [],
      offTimers: [],
      onDays: undefined,
      holidayInterval,
      now
    };
  }

  const { onDays, weekDayIntervals } = power;
  if (holidayIntervals.length > 0) {
    holidayInterval = currentOrNextMergedInterval(
      holidayIntervals,
      onDays,
      today
    );
  }

  const [offTimers, onTimers] = getTimersByPowerState(weekDayIntervals, now);
  return { onTimers, offTimers, onDays, holidayInterval, now };
}

/**
 * @param {IContentInfo} contentInfo
 * @param {ISOLocalString} now
 * @returns {TimersAndHoliday}
 */
function getTimersAndHolidayWithReverseShutdown(contentInfo, now) {
  const timersAndHoliday = getTimersAndHoliday(contentInfo, now);
  const future = addMs(now, reverseShutdownDelay);

  return shouldReverseShutdown(contentInfo, future)
    ? insertReverseShutdownTimer(timersAndHoliday, future)
    : timersAndHoliday;
}

/**
 * Vypočítá plán probuzení s jedním ON timerem a prázdninovým intervalem,
 * a OFF timerem pro okamžité vypnutí.
 * @param {ISOLocalString} date
 * @param {ISOLocalString} now
 * @returns {TimersAndHoliday}
 */
function getWakeUpSchedule(date, now) {
  return {
    onTimers: [getWakeUpOnTimer(date, now)],
    offTimers: [createTimer(now, 'off')],
    // Nezáleží, den probuzení určuje ON timer.
    onDays: SomeDaysOfWeek.from(127),
    holidayInterval: getSleepDayInterval(date, now),
    now
  };
}

/**
 * Vypočítá ON timer pro probuzení.
 * Pokud je rozdíl mezi daty větší nebo roven {@link maxDays} dnům,
 * nastaví den probuzení na {@link maxDays} + 1 den (aby bylo jisté,
 * že prázdninový interval skončí před dnem probuzení).
 * @param {ISOLocalString} date
 * @param {ISOLocalString} now
 * @returns {PowerTimer}
 */
function getWakeUpOnTimer(date, now) {
  const targetDayDate = getDayDate(date);
  const nowDayDate = getDayDate(now);
  let day = getDayOfWeek(targetDayDate);
  if (diffDays(targetDayDate, nowDayDate) > maxDays) {
    // "zítra" + 42 dní + 1 den po
    day = getDayOfWeek(addDays(nowDayDate, maxDays + 1));
  }
  return { state: 'on', day, time: getISOTimeString(date) };
}

/**
 * @param {TimersAndHoliday} timersAndHoliday
 * @param {ISOLocalString} date
 * @returns {TimersAndHoliday}
 * @throws {AssertionError} Pokud se pokusí vložit reverse shutdown timer pro 24/7 plán.
 */
function insertReverseShutdownTimer(timersAndHoliday, date) {
  if (timersAndHoliday.onDays === undefined) {
    throw new AssertionError(
      'Cannot insert reverse shutdown timer for 24/7 schedule.'
    );
  }
  const reverseShutdownTimer = createTimer(date, 'off');
  const { offTimers } = timersAndHoliday;
  if (offTimers.length >= 7) {
    // keď to aj tak vypneme za 10 minút, teoreticky je jedno, aký off timer nahradíme:
    offTimers.pop();
  }
  offTimers.push(reverseShutdownTimer);
  return { ...timersAndHoliday, offTimers };
}

/**
 * @param {DayDate} dayDate
 * @param {DayInterval | null} holidayInterval
 * @returns {boolean}
 */
function isHoliday(dayDate, holidayInterval) {
  return (
    holidayInterval !== null &&
    !isLessDayDate(dayDate, holidayInterval.start) &&
    !isLessDayDate(holidayInterval.end, dayDate)
  );
}

/**
 * Pokud je `onDays` nedefinováno, znamená to režim 24/7 bez časovačů, takže nemá spát.
 *
 * Pokud je `onDays` definováno:
 *  - a dnes není zapnutý den v týdnu, pak dnes není žádny časovač, (a tím pádem) nemá spát.
 *  - a dnes je i zapnutý den v týdnu, pak dnes je nějaký časovač, pak a může spát, pokud je nejblížší časovač ON.
 *
 * Pro určení, zda má zařízení spát, platí následující pravidla s ohledem k naplánovaným časovačům pro konkrétní den:
 * - pokud není žádný OFF timer, zařízení má spát (buď není naplánován žádný ON timer, nebo již dnes není nic naplánováno).
 * - pokud je OFF timer a není ON timer, zařízení nemá spát (je v aktivním intervalu).
 * - pokud jsou oba timery, zařízení má spát pouze tehdy, když je nejbližší ON timer naplánován před nejbližším OFF timerem.
 * @param {TimersAndHoliday} timersAndHoliday
 * @returns {boolean}
 * @throws {AssertionError} Pokud jsou porušeny předpoklady o parametrech.
 */
function isPowerOffScheduled(timersAndHoliday) {
  const { offTimers, onTimers, onDays, now } = timersAndHoliday;
  const dayOfWeek = getDayOfWeek(getDayDate(now));
  const nowTime = getISOTimeString(now);
  const activeOnTimers = getActiveTimersForDay(onTimers, dayOfWeek, nowTime);
  const activeOffTimers = getActiveTimersForDay(offTimers, dayOfWeek, nowTime);
  return (
    onDays !== undefined &&
    (activeOffTimers.length === 0 ||
      (activeOnTimers.length > 0 &&
        isLessISOTime(activeOnTimers[0].time, activeOffTimers[0].time)))
  );
}

/**
 * @param {IContentInfo} contentInfo
 * @param {ISOLocalString} date
 * @returns {boolean}
 */
function shouldReverseShutdown(contentInfo, date) {
  if (!contentInfo?.power?.reverseOne) {
    return false;
  }
  const dayDate = getDayDate(date);
  const timersAndHoliday = getTimersAndHoliday(contentInfo, date);
  return (
    isHoliday(dayDate, timersAndHoliday.holidayInterval) ||
    isPowerOffScheduled(timersAndHoliday)
  );
}

/**
 * @param {ISOLocalString} date
 * @param {ISOLocalString} now
 * @throws {RangeError} Pokud je datum probuzení v minulosti nebo příliš blízko (1 minuta).
 */
function validateWakeUpDate(date, now) {
  const dateTime = getISOTimeString(date);
  const nowTime = getISOTimeString(now);
  const targetDayDate = getDayDate(date);
  const nowDayDate = getDayDate(now);
  if (
    isLessDayDate(targetDayDate, nowDayDate) ||
    (targetDayDate === nowDayDate &&
      getMillisecondsOfDay(dateTime) - getMillisecondsOfDay(nowTime) <=
        safetyPadding)
  ) {
    throw new RangeError(
      `Invalid wake-up date "${date}" (now: "${now}"): must be at least 1 minute in the future.`
    );
  }
}

export const internal = {
  currentOrNextMergedInterval,
  getNextOffTimer,
  getTimeoutDuration,
  getTimersOfCurrentDay,
  getTimersAndHoliday,
  getTimersAndHolidayWithReverseShutdown,
  insertReverseShutdownTimer,
  isPowerOffScheduled,
  shouldReverseShutdown,
  reverseShutdownDelay
};
